import { get, setMany, delMany } from 'idb-keyval';
import type { SaveAdapter } from './SaveAdapter';
import type { PlayerSave } from '../PlayerSave.schema';

/**
 * Adapter persistence przez IndexedDB (przez `idb-keyval`).
 *
 * **Double-buffered pattern, atomic write:**
 * Trzymamy DWA pełne save'y (sloty A i B) + meta. Zapis ZAWSZE do starszego
 * slotu + meta razem przez `setMany` — single IDB transaction = atomic.
 * Crash w środku nie zostawia mismatchu meta↔slot.
 *
 * **Load defensywnie:** czytamy oba sloty i wybieramy po `lastSavedAt` (max).
 * Meta jest tylko hint'em — gdyby meta wskazywała stary slot mimo nowszych
 * danych w drugim (po atomic write powinno się nie zdarzyć, ale defensywnie
 * dla save'ów ze starszego kodu).
 *
 * **Mutex na save():** kolejne `save()` callsy są serializowane przez Promise
 * chain. Dwa wątki (np. autosave + manual save) nie czytają tej samej meta
 * i nie nadpisują sobie nawzajem.
 *
 * **Error handling:** rozróżniamy "brak save'a" (return null) od "storage
 * unavailable" (throw `StorageUnavailableError`). SaveManager interpretuje:
 *   - null → fresh save dla gracza
 *   - throw → in-memory degraded mode (NIE nadpisuje istniejącego save'a)
 *
 * IndexedDB limity: ~50MB w większości browserów, save ~10-50KB nawet z setkami
 * petów. Margines x1000.
 */

const KEY_A = 'pc-save-a';
const KEY_B = 'pc-save-b';
const KEY_META = 'pc-save-meta';

interface SaveMeta {
  lastWritten: 'a' | 'b';
  aTimestamp?: number;
  bTimestamp?: number;
}

/** Thrown gdy IndexedDB nie jest dostępne (incognito, quota, korupcja). */
export class StorageUnavailableError extends Error {
  constructor(cause: unknown) {
    super(`IndexedDB unavailable: ${(cause as Error)?.message ?? String(cause)}`);
    this.name = 'StorageUnavailableError';
  }
}

export class LocalAdapter implements SaveAdapter {
  /**
   * Promise chain — każdy save() linkuje się na koniec, żeby concurrent calls
   * były serializowane. `.catch(() => {})` zapobiega temu, żeby błąd jednego
   * save'a blokował kolejne (każdy save samodzielnie raportuje swój błąd).
   */
  private saveLock: Promise<unknown> = Promise.resolve();

  async load(): Promise<PlayerSave | null> {
    try {
      const meta = await get<SaveMeta>(KEY_META);
      if (!meta) return null;       // czysta sytuacja: brak save'a, gracz nowy

      // Czytamy OBA sloty równolegle
      const [slotA, slotB] = await Promise.all([
        get<PlayerSave>(KEY_A),
        get<PlayerSave>(KEY_B),
      ]);

      // Wybieramy nowszy plausible (defensywa przed mismatchem meta↔slot)
      const candidates: Array<{ save: PlayerSave; ts: number }> = [];
      if (slotA && this.isPlausibleSave(slotA)) {
        candidates.push({ save: slotA, ts: slotA.lastSavedAt ?? 0 });
      }
      if (slotB && this.isPlausibleSave(slotB)) {
        candidates.push({ save: slotB, ts: slotB.lastSavedAt ?? 0 });
      }

      if (candidates.length === 0) {
        console.error('[LocalAdapter] Oba sloty corrupt/missing mimo meta — fresh save');
        return null;
      }

      // Sortuj malejąco po timestamp, weź najnowszy
      candidates.sort((x, y) => y.ts - x.ts);
      const winner = candidates[0]!;

      if (candidates.length === 2) {
        const metaPreferred = meta.lastWritten === 'a' ? slotA : slotB;
        if (metaPreferred !== winner.save) {
          console.warn('[LocalAdapter] Meta wskazuje starszy slot — używam nowszego po timestamp');
        }
      }

      return winner.save;
    } catch (err) {
      throw new StorageUnavailableError(err);
    }
  }

  /**
   * Public API — kolejne save() są kolejkowane przez `saveLock` mutex.
   * Zwraca Promise który resolve'uje gdy aktualny save się skończy.
   */
  async save(state: PlayerSave): Promise<void> {
    // .catch(() => {}) na poprzednim — błąd jednego save'a nie zablokuje kolejnego.
    // Nasz nowy doSave throwuje swój błąd niezależnie.
    const next = this.saveLock.catch(() => {}).then(() => this.doSave(state));
    this.saveLock = next;
    return next;
  }

  private async doSave(state: PlayerSave): Promise<void> {
    try {
      const meta = await get<SaveMeta>(KEY_META);

      // Zapis do STARSZEGO slotu (lub do A jeśli pierwsze uruchomienie)
      const targetSlot: 'a' | 'b' = meta?.lastWritten === 'a' ? 'b' : 'a';
      const targetKey = targetSlot === 'a' ? KEY_A : KEY_B;

      const newMeta: SaveMeta = {
        lastWritten: targetSlot,
        aTimestamp: targetSlot === 'a' ? Date.now() : meta?.aTimestamp,
        bTimestamp: targetSlot === 'b' ? Date.now() : meta?.bTimestamp,
      };

      // ⭐ ATOMIC WRITE — slot + meta razem w jednej IDB transaction.
      // Crash w środku → wszystko rolluje back, stary save nietknięty.
      // Eliminuje window między set(slot) a set(meta) z poprzedniej implementacji.
      await setMany([
        [targetKey, state],
        [KEY_META, newMeta],
      ]);
    } catch (err) {
      throw new StorageUnavailableError(err);
    }
  }

  /**
   * Usuwa oba sloty + meta w jednej IDB transaction (`delMany`).
   * Po `clear()` następne `load()` zwróci `null` — gracz dostanie fresh save.
   *
   * Idzie przez `saveLock` jak `save()` żeby concurrent save + clear nie
   * scrambled state. Gdyby save był w locie, najpierw skończy, potem clear
   * — i wtedy nadpisane dane też znikną.
   *
   * Sesja 23c: dodane po Codex finding (dev helper hardcode'ował klucze).
   */
  async clear(): Promise<void> {
    const next = this.saveLock.catch(() => {}).then(async () => {
      try {
        await delMany([KEY_A, KEY_B, KEY_META]);
      } catch (err) {
        throw new StorageUnavailableError(err);
      }
    });
    this.saveLock = next;
    return next;
  }

  /** Sprawdza podstawowe pola schema'y. Pełna walidacja = zadanie migracji. */
  private isPlausibleSave(save: unknown): save is PlayerSave {
    if (typeof save !== 'object' || save === null) return false;
    const s = save as Record<string, unknown>;
    return (
      typeof s.version === 'number' &&
      typeof s.lastSavedAt === 'number' &&
      typeof s.profile === 'object' &&
      typeof s.currencies === 'object'
    );
  }
}
